import _styled, { default as _styled2, default as _styled3, default as _styled4, default as _styled5, default as _styled6, default as _styled7 } from "styled-components";
import { AnimatePresence, m } from "framer-motion";
import React from "react";
import { type Mode } from "~/features/ui/get-theme-style";
import { getSubNavThemeStyle } from "~/features/sub-nav/sub-nav-theme";
import { hasItems } from "~/features/app/utils";
import { scrollElementIntoView } from "~/features/app/utils";
import { useMediaQuery } from "~/features/ui/use-media-query";
import { up } from "~/features/app/screens";
import { useSubNavLayout } from "~/features/sub-nav/context";
import { useScrollableAnchors } from "~/features/sub-nav/use-scrollable-anchors";
export type Anchor = {
  id: string;
  name: string;
};
type Props = {
  anchors: Anchor[];
  activeSectionId?: string;
};
const hoverStyle = {
  light: {
    "@media (hover: hover)": {
      "&:hover": {
        "--tw-bg-opacity": "1",
        "backgroundColor": "rgb(208 208 218 / var(--tw-bg-opacity))"
      },
      "&:hover::before": {
        "content": "var(--tw-content)",
        "opacity": "1"
      }
    }
  },
  dark: {
    "@media (hover: hover)": {
      "&:hover": {
        "--tw-bg-opacity": "1",
        "backgroundColor": "rgb(92 92 92 / var(--tw-bg-opacity))"
      },
      "&:hover::before": {
        "content": "var(--tw-content)",
        "opacity": "1"
      }
    }
  }
} as const;
const StyledScrollOverlay = _styled.div.withConfig({
  componentId: "sc-sf-7zj4il-0"
})<{
  $mode: Mode;
  $position: "right" | "left";
}>`
  ${{
  "position": "absolute",
  "top": "0",
  "bottom": "0",
  "zIndex": "10",
  "width": "5.6rem"
}}

  ${({
  $mode
}) => $mode === "dark" && {
  "--tw-gradient-from": "#000704 var(--tw-gradient-from-position)",
  "--tw-gradient-to": "rgb(0 7 4 / 0) var(--tw-gradient-to-position)",
  "--tw-gradient-stops": "var(--tw-gradient-from), var(--tw-gradient-to)"
}}
  ${({
  $mode
}) => $mode === "light" && {
  "--tw-gradient-from": "#EFEFF6 var(--tw-gradient-from-position)",
  "--tw-gradient-to": "rgb(239 239 246 / 0) var(--tw-gradient-to-position)",
  "--tw-gradient-stops": "var(--tw-gradient-from), var(--tw-gradient-to)"
}}

  ${({
  $position
}) => $position === "right" && {
  "right": "0",
  "backgroundImage": "linear-gradient(to left, var(--tw-gradient-stops))"
}}
  ${({
  $position
}) => $position === "left" && {
  "left": "0",
  "backgroundImage": "linear-gradient(to right, var(--tw-gradient-stops))"
}}
`;
export function SubNavAnchors(props: Props & React.ComponentPropsWithRef<"div">) {
  // Guard before any hooks: `useScrollableAnchors` sets up `useScroll({ container: ref })`,
  // which throws "Container ref is defined but not hydrated" if the scrollable `ref` element
  // never mounts. Only render the hook-bearing body when there are anchors to attach it to.
  if (!hasItems(props.anchors)) {
    return null;
  }
  return <ScrollableAnchors {...props} />;
}
function ScrollableAnchors(props: Props & React.ComponentPropsWithRef<"div">) {
  const {
    anchors,
    activeSectionId
  } = props;
  const {
    subNavMode
  } = useSubNavLayout();
  const themeStyle = getSubNavThemeStyle(subNavMode);
  const ref = React.useRef<HTMLDivElement>(null);
  const isLg = useMediaQuery(up("lg"));
  const {
    isScrollable,
    scrollState
  } = useScrollableAnchors({
    activeSectionId: activeSectionId || "",
    ref
  });
  return <_StyledDiv>
      {isScrollable && scrollState !== "END" && <StyledScrollOverlay $mode={subNavMode} $position="right" />}
      {isScrollable && scrollState !== "START" && <StyledScrollOverlay $mode={subNavMode} $position="left" />}
      <_StyledDiv2 ref={ref} className="hide-scrollbar" $_css={[{
      "position": "relative",
      "height": "5.6rem",
      "width": "100%",
      "justifyContent": "center",
      "overflowX": "auto",
      "borderBottomWidth": "1px",
      "fontSize": "1.6rem",
      "lineHeight": "1.5",
      "fontWeight": "400",
      "@media (min-width: 1024px)": {
        "height": "7.2rem"
      }
    }, themeStyle.border]}>
        <_StyledUl>
          {anchors.map((anchor, idx) => <_StyledLi key={idx} id={`anchor-${anchor.id}`} $_css2={[{
          "position": "relative",
          "height": "100%",
          "transitionProperty": "color, background-color, border-color, text-decoration-color, fill, stroke",
          "transitionDuration": "250ms",
          "transitionTimingFunction": "ease-in-out",
          "&::before": {
            "content": "var(--tw-content)",
            "position": "absolute",
            "left": "0",
            "right": "0",
            "top": "0",
            "height": "0.4rem",
            "--tw-bg-opacity": "1",
            "backgroundColor": "rgb(75 46 248 / var(--tw-bg-opacity))",
            "opacity": "0",
            "transitionProperty": "opacity",
            "transitionDuration": "250ms",
            "transitionTimingFunction": "ease-in-out",
            "--tw-content": "\"\""
          }
        }, hoverStyle[subNavMode]]}>
              <AnimatePresence>
                {anchor.id === activeSectionId && <_StyledMDiv layoutId="subnav-anchor" transition={{
              duration: 0.2
            }} />}
              </AnimatePresence>
              <_StyledButton onClick={() => scrollElementIntoView(anchor.id!, isLg ? 72 : 112)} aria-label={`Go to ${anchor.name}`}>
                {anchor.name}
              </_StyledButton>
            </_StyledLi>)}
        </_StyledUl>
      </_StyledDiv2>
    </_StyledDiv>;
}
var _StyledDiv = _styled2("div").withConfig({
  componentId: "sc-sf-7zj4il-1"
})({
  "position": "relative",
  "isolation": "isolate",
  "flex": "1 1 0%",
  "overflow": "hidden"
});
var _StyledDiv2 = _styled3("div").withConfig({
  componentId: "sc-sf-7zj4il-2"
})`${p => p.$_css}`;
var _StyledUl = _styled4("ul").withConfig({
  componentId: "sc-sf-7zj4il-3"
})({
  "display": "flex",
  "height": "100%"
});
var _StyledLi = _styled5("li").withConfig({
  componentId: "sc-sf-7zj4il-4"
})`${p => p.$_css2}`;
var _StyledMDiv = _styled6(m.div).withConfig({
  componentId: "sc-sf-7zj4il-5"
})({
  "position": "absolute",
  "left": "0",
  "right": "0",
  "top": "0",
  "zIndex": "20",
  "height": "0.4rem",
  "--tw-bg-opacity": "1",
  "backgroundColor": "rgb(75 46 248 / var(--tw-bg-opacity))"
});
var _StyledButton = /*#__PURE__*/_styled7("button").withConfig({
  displayName: "sub-nav-anchors___StyledButton",
  componentId: "sc-p3m3mv-0"
})({
  "height": "100%",
  "whiteSpace": "nowrap",
  "paddingLeft": "2.4rem",
  "paddingRight": "2.4rem",
  "@media (min-width: 1024px)": {
    "paddingLeft": "4rem",
    "paddingRight": "4rem"
  }
});